import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { toast } from "react-toastify";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import CustomButton from "../pages/CustomButton";
import formatDate from "../utils/formatDate";
import XLSX from "xlsx-js-style";

const PobFY = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);

  // default to "All" selections where appropriate
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [financialYear, setFinancialYear] = useState("");
  const [financialYearOptions, setFinancialYearOptions] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generate Financial Years from 2024-25 to current+1
  const generateFinancialYears = () => {
    const startYear = 2024;
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const fyList = [];

    for (let year = startYear; year <= currentYear + 1; year++) {
      fyList.push(`${year}-${year + 1}`);
    }

    const defaultFY =
      currentMonth >= 4
        ? `${currentYear}-${currentYear + 1}`
        : `${currentYear - 1}-${currentYear}`;

    setFinancialYearOptions(fyList);
    setFinancialYear(defaultFY);
  };

  useEffect(() => {
    generateFinancialYears();
  }, []);

  // Flexible fetchEmployees that supports stateId = 'All' (no filter)
  const fetchEmployees = useCallback(
    async (stateId = "All", divisionName = "All") => {
      try {
        let url = `${BASE_URL}/api/employees`;
        const params = [];
        if (stateId && stateId !== "All") params.push(`state_id=${stateId}`);
        if (divisionName && divisionName !== "All")
          params.push(`division=${encodeURIComponent(divisionName)}`);
        if (params.length) url += `?${params.join("&")}`;

        const res = await axios.get(url, { headers: getAuthHeaders() });
        setEmployees([
          { id: "All", first_name: "All", last_name: "" },
          ...res.data,
        ]);
        setSelectedEmployee("All");
      } catch (err) {
        toast.error("Failed to load employees");
        setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
      }
    },
    [],
  );

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        // add an All option and default select to All
        const statesWithAll = [{ id: "All", name: "All" }, ...response.data];
        setStates(statesWithAll);
        setSelectedState("All");

        // fetch divisions and employees with no-state filter (All)
        fetchDivisions("All");
        fetchEmployees("All", "All");
      } catch (err) {
        toast.error("Failed to load states");
        setError("Failed to load states");
      }
    };
    fetchStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEmployees]);

  // fetch divisions, supports stateId = 'All'
  const fetchDivisions = async (stateId = "All") => {
    try {
      let url = `${BASE_URL}/api/divisions`;
      if (stateId && stateId !== "All") url += `?state_id=${stateId}`;
      const divisionRes = await axios.get(url, { headers: getAuthHeaders() });
      setDivisions([{ id: "All", name: "All" }, ...divisionRes.data]);
      setSelectedDivision("All");
    } catch (err) {
      toast.error("Failed to load divisions");
      setDivisions([{ id: "All", name: "All" }]);
    }
  };

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setShowContainers(false);

    // fetch divisions based on state (or all if 'All')
    fetchDivisions(stateId);

    // fetch employees for state (and All division)
    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setShowContainers(false);

    const division = divisions.find((d) => String(d.id) === String(divisionId));
    const divisionName = division?.name || "All";

    fetchEmployees(selectedState, divisionName);
  };

  // Fetch report data. Allow 'All' selections by omitting query params when 'All'.
  const fetchData = useCallback(async () => {
    // allow fetching even when selectedState is 'All' (don't block)
    try {
      setIsLoading(true);
      setError(null);

      let url = `${BASE_URL}/api/pob-targets/pob_target_report?`;

      if (selectedState && selectedState !== "All")
        url += `state_id=${selectedState}&`;
      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find(
          (d) => String(d.id) === String(selectedDivision),
        );
        if (division?.name)
          url += `division=${encodeURIComponent(division.name)}&`;
      }
      if (selectedEmployee && selectedEmployee !== "All")
        url += `employee_id=${selectedEmployee}&`;
      if (financialYear)
        url += `financial_year=${encodeURIComponent(financialYear)}&`;

      url = url.replace(/&$/, ""); // Remove trailing &

      const response = await axios.get(url, { headers: getAuthHeaders() });

      if (response.data) {
        if (Array.isArray(response.data)) {
          setTableData(response.data);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          setTableData(response.data.data);
        } else {
          setTableData([]);
        }
      } else {
        setTableData([]);
      }

      setShowContainers(true);
    } catch (err) {
      toast.error("Failed to fetch report data");
      if (err.response) {
        if (err.response.status === 422) {
          setError(
            `Validation error: ${JSON.stringify(err.response.data.detail)}`,
          );
        } else {
          setError(
            `Server error: ${err.response.status} - ${err.response.statusText}`,
          );
        }
      } else if (err.request) {
        setError("No response received from server");
      } else {
        setError(`Request error: ${err.message}`);
      }
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedState,
    selectedDivision,
    selectedEmployee,
    financialYear,
    divisions,
  ]);

  const handleGoClick = () => {
    fetchData();
  };

  const handlePrint = () => {
    const tableEl = document.querySelector("table");
    if (!tableEl) {
      toast.info("No table available to print");
      return;
    }
    const clonedTable = tableEl.cloneNode(true);
    clonedTable.querySelectorAll("button, input, a").forEach((el) => {
      const span = document.createElement("span");
      span.innerHTML = el.textContent || el.innerHTML || "";
      el.parentNode.replaceChild(span, el);
    });

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>POB FY Report - ${financialYear || "All"}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial; color:#111; padding:16px; }
            h1 { text-align: center; margin-bottom: 12px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; table-layout: auto; }
            th, td { border: 1px solid #999; padding: 6px 8px; vertical-align: top; word-break: break-word; }
            thead th { background: #d6f0f5; color: #0b2540; font-weight: 700; position: sticky; top: 0; }
            tr:nth-child(even) { background: #f9f9f9; }
            @media print {
              thead { display: table-header-group; }
              tr { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>POB FY Report - ${financialYear || "All"}</h1>
          ${clonedTable.outerHTML}
          <div style="margin-top:12px; text-align:center; font-size:11px;">Generated on: ${new Date().toLocaleString()}</div>
        </body>
      </html>
    `;

    const w = window.open("", "_blank", "width=1200,height=800");
    if (!w) {
      toast.warning("Please allow popups to print");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      try {
        w.print();
      } catch (e) {
        toast.error("Printing failed");
      }
    }, 500);
  };
const handleExport = () => {
  if (!tableData || tableData.length === 0) {
    toast.info("No data to export");
    return;
  }

  try {
    const wb = XLSX.utils.book_new();
    const ws = {};

    const headers = [
      "S.No", "Emp ID", "State", "Employee", "Designation",
      "HQ", "Division", "DOJ", "DOR", "Manager",
      "Apr POB", "May POB", "Jun POB", "Jul POB", "Aug POB",
      "Sep POB", "Oct POB", "Nov POB", "Dec POB", "Jan POB",
      "Feb POB", "Mar POB", "Total POB",
    ];

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
      fill: { fgColor: { rgb: "2563EB" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    const rowStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    // Header row
    headers.forEach((header, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      ws[cellAddress] = { v: header, t: "s", s: headerStyle };
    });

    // Data rows
    tableData.forEach((row, index) => {
      const excelRow = index + 2;

      const empRaw = String(row.employee ?? "").trim();
      const empIsNum = /^\d+$/.test(empRaw);
      const empID = empIsNum ? Number(empRaw) : empRaw;

      const rowData = [
        index + 1,
        empID,
        row.state || "",
        row.employee_name || "",
        row.designation || "",
        row.headquarter || "-",
        row.division || "",
        null,  // col 7 — DOJ (patched below)
        null,  // col 8 — DOR (patched below)
        row.manager || "-",
        Number(row.apr_pob_achievement || 0),
        Number(row.may_pob_achievement || 0),
        Number(row.jun_pob_achievement || 0),
        Number(row.jul_pob_achievement || 0),
        Number(row.aug_pob_achievement || 0),
        Number(row.sep_pob_achievement || 0),
        Number(row.oct_pob_achievement || 0),
        Number(row.nov_pob_achievement || 0),
        Number(row.dec_pob_achievement || 0),
        Number(row.jan_pob_achievement || 0),
        Number(row.feb_pob_achievement || 0),
        Number(row.mar_pob_achievement || 0),
        0, // Total — formula below
      ];

      rowData.forEach((value, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: index + 1, c: colIndex });

        // ✅ DOJ (col 7) and DOR (col 8) — real Excel dates
        if (colIndex === 7 || colIndex === 8) {
          const rawVal = colIndex === 7 ? row.final_joining_date : row.resign_date;
          if (rawVal) {
            try {
              const date = new Date(
                /^\d{4}-\d{2}-\d{2}$/.test(String(rawVal))
                  ? `${rawVal}T00:00:00`
                  : rawVal
              );
              if (!isNaN(date.getTime())) {
                ws[cellAddress] = { v: date, t: "d", z: "dd-mmm-yyyy", s: rowStyle };
                return;
              }
            } catch {}
          }
          ws[cellAddress] = { v: "", t: "s", s: rowStyle };
          return;
        }

        ws[cellAddress] = {
          v: value,
          t:
            colIndex === 1
              ? empIsNum ? "n" : "s"
              : typeof value === "number" ? "n" : "s",
          s: rowStyle,
        };
      });

      // Total formula
      ws[`W${excelRow}`] = {
        f: `SUM(K${excelRow}:V${excelRow})`,
        t: "n",
        s: rowStyle,
      };
    });

    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: tableData.length, c: headers.length - 1 },
    });

    ws["!cols"] = [
      { wch: 8 }, { wch: 12 }, { wch: 15 }, { wch: 30 }, { wch: 18 },
      { wch: 18 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 25 },
      ...Array.from({ length: 13 }, () => ({ wch: 14 })),
    ];

    ws["!freeze"] = { xSplit: 0, ySplit: 1 };
    ws["!autofilter"] = { ref: "A1:W1" };

    XLSX.utils.book_append_sheet(wb, ws, "POB FY Report");

    const fname = `pobfy_${financialYear || "all"}_${new Date()
      .toISOString().slice(0, 10)}.xlsx`;

    XLSX.writeFile(wb, fname, { compression: true });

  } catch (err) {
    toast.error("Export failed. Please try again.");
  }
};

  return (
    <div className="p-4 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">POB </span>
          <span className="text-blue-500">FY Report</span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
          <label className="text-right text-sm text-gray-700">State</label>
          <select
            className={`w-full mt-1 border rounded px-2 py-1 text-sm ${error ? "ring-2 ring-red-400" : ""
              }`}
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
          >
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="text-right text-sm text-gray-700">Division</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-sm"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={!selectedState || isLoading}
          >
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center mt-4">
          <label className="text-right text-sm text-gray-700">Employee</label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-sm"
            value={selectedEmployee}
            onChange={(e) => {
              setSelectedEmployee(e.target.value);
              setShowContainers(false);
            }}
            disabled={!selectedState || isLoading}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All"
                  ? "All"
                  : `${emp.first_name} ${emp.last_name} | ${emp.employee} | ${emp.designation || "-"} | ${emp.division || "-"} | ${emp.state_name || "-"}`}
              </option>
            ))}
          </select>

          <label className="text-right text-sm text-gray-700">
            Financial Year
          </label>
          <select
            className="w-full mt-1 border rounded px-2 py-1 text-sm"
            value={financialYear}
            onChange={(e) => {
              setFinancialYear(e.target.value);
              setShowContainers(false);
            }}
            disabled={isLoading}
          >
            {financialYearOptions.map((fy, idx) => (
              <option key={idx} value={fy}>
                {fy}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-center mt-4 gap-4">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>

        {error && <div className="mt-3 text-center text-red-600">{error}</div>}
      </div>

      {showContainers && (
        <div className="mt-6">
          {tableData.length === 0 && !isLoading && (
            <div className="text-center py-4 text-gray-500">
              No records found. Please try different filters.
            </div>
          )}

          {tableData.length > 0 && (
            <div className="border rounded shadow  table-wrapper">
              <table className="unlock-dcr-leave-table text-sm ">
                <thead className="bg-blue-500 text-white">
                  <tr>
                    <th className="px-3 py-2 border text-center">S.No</th>
                    <th className="px-3 py-2 border text-center">Emp ID</th>
                    <th className="px-3 py-2 border text-center">State</th>
                    <th className="px-3 py-2 border text-left">Employee</th>
                    <th className="px-3 py-2 border text-center">
                      Designation
                    </th>
                    <th className="px-3 py-2 border text-center">HQ</th>
                    <th className="px-3 py-2 border text-center">Division</th>
                    <th className="px-3 py-2 border text-center">DOJ</th>
                    <th className="px-3 py-2 border text-center">DOR</th>
                    <th className="px-3 py-2 border text-center">Manager</th>
                    <th className="px-3 py-2 border text-center">Apr POB</th>
                    <th className="px-3 py-2 border text-center">May POB</th>
                    <th className="px-3 py-2 border text-center">Jun POB</th>
                    <th className="px-3 py-2 border text-center">Jul POB</th>
                    <th className="px-3 py-2 border text-center">Aug POB</th>
                    <th className="px-3 py-2 border text-center">Sep POB</th>
                    <th className="px-3 py-2 border text-center">Oct POB</th>
                    <th className="px-3 py-2 border text-center">Nov POB</th>
                    <th className="px-3 py-2 border text-center">Dec POB</th>
                    <th className="px-3 py-2 border text-center">Jan POB</th>
                    <th className="px-3 py-2 border text-center">Feb POB</th>
                    <th className="px-3 py-2 border text-center">Mar POB</th>
                    <th className="px-3 py-2 border text-center">Total POB</th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y">
                  {tableData.map((row, index) => (
                    <tr
                      key={row.employee || index}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-3 py-2 border text-center align-middle">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 border text-center align-middle">
                        {row.employee}
                      </td>
                      <td className="px-3 py-2 border text-center align-middle">
                        {row.state}
                      </td>
                      <td className="px-3 py-2 border text-left align-middle">
                        {row.employee_name}
                      </td>
                      <td className="px-3 py-2 border text-center align-middle">
                        {row.designation}
                      </td>
                      <td className="px-3 py-2 border text-center align-middle">
                        {row.headquarter || "-"}
                      </td>
                      <td className="px-3 py-2 border text-center align-middle">
                        {row.division}
                      </td>
                      <td className="px-3 py-2 border text-center align-middle">
                        {formatDate(row.final_joining_date) || "-"}
                      </td>
                      <td className="px-3 py-2 border text-center align-middle">
                        {formatDate(row.resign_date) || "-"}
                      </td>
                      <td className="px-3 py-2 border text-center align-middle">
                        {row.manager || "-"}
                      </td>
                      <td>{row.apr_pob_achievement ?? 0}</td>
                      <td>{row.may_pob_achievement ?? 0}</td>
                      <td>{row.jun_pob_achievement ?? 0}</td>
                      <td>{row.jul_pob_achievement ?? 0}</td>
                      <td>{row.aug_pob_achievement ?? 0}</td>
                      <td>{row.sep_pob_achievement ?? 0}</td>
                      <td>{row.oct_pob_achievement ?? 0}</td>
                      <td>{row.nov_pob_achievement ?? 0}</td>
                      <td>{row.dec_pob_achievement ?? 0}</td>
                      <td>{row.jan_pob_achievement ?? 0}</td>
                      <td>{row.feb_pob_achievement ?? 0}</td>
                      <td>{row.mar_pob_achievement ?? 0}</td>

                      <td className="px-3 py-2 border text-center align-middle">
                        {row.pob || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton
              onClick={handleExport}
              label="Export"
              variant="export"
            >
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default PobFY;
